import { Link, useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import StoryCard from "@/components/feature/StoryCard";
import { COLUMNS } from "@/mocks/site";
import { columnBySlug, storiesOfColumn } from "@/lib/content";

export default function ColumnPage() {
  const { slug = "" } = useParams();
  const column = columnBySlug(slug);
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  if (!column) {
    return (
      <section className="mx-auto max-w-3xl px-4 py-20 text-center md:px-6">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-secondary-100 text-3xl text-secondary-700">
          <i className="ri-error-warning-line" aria-hidden="true" />
        </span>
        <h1 className="mt-5 font-heading text-2xl font-black text-foreground-950">没有找到这个栏目</h1>
        <p className="mt-3 text-sm text-foreground-600">栏目可能已调整，去看看其他瓜田吧。</p>
        <Link to="/" className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600">
          <i className="ri-home-line" aria-hidden="true" />
          返回首页
        </Link>
      </section>
    );
  }

  const stories = storiesOfColumn(slug);
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: origin + "/" },
        { "@type": "ListItem", position: 2, name: column.name, item: origin + `/column/${slug}` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: `${column.name} - 大学吃瓜`,
      description: column.desc,
      url: origin + `/column/${slug}`,
    },
  ];

  return (
    <>
      <Seo
        title={`${column.name}_大学吃瓜${column.name}专区`}
        description={`${column.name}专区：${column.desc} 大学吃瓜${column.name}故事，${column.tagline}，最新内容天天更新。`}
        keywords={`大学吃瓜${column.name},校园${column.name},大学吃瓜`}
        type="article"
        jsonLd={jsonLd}
      />
      {/* banner */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={column.image} alt={`${column.name}栏目背景`} className="h-full w-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/70 to-foreground-950/40" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-sm text-background-200/90">
            <Link to="/" className="flex items-center gap-1 hover:text-background-50"><i className="ri-home-line text-base" aria-hidden="true" />首页</Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true" />
            <span aria-current="page" className="text-background-50">{column.name}</span>
          </nav>
          <div className="mt-5 max-w-2xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-background-50">
              {column.en}
            </span>
            <h1 className="mt-4 font-heading text-3xl font-black leading-tight text-background-50 md:text-4xl">{column.name}</h1>
            <p className="mt-3 text-base leading-7 text-background-100/95">{column.desc}</p>
            <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-background-100/90">
              <span className="flex items-center gap-1.5"><i className="ri-article-line" aria-hidden="true" />{stories.length} 篇瓜文</span>
              <span className="flex items-center gap-1.5"><i className="ri-fire-line" aria-hidden="true" />持续热更</span>
            </div>
          </div>
        </div>
      </section>

      {/* other columns quick switch */}
      <section className="mx-auto max-w-7xl px-4 py-8 md:px-6">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-foreground-700">换口味看：</span>
          {COLUMNS.filter((c) => c.slug !== slug).map((c) => (
            <Link
              key={c.slug}
              to={`/column/${c.slug}`}
              className="whitespace-nowrap rounded-full border border-background-300 bg-background-100 px-4 py-1.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              {c.name}
            </Link>
          ))}
        </div>
      </section>

      {/* story list */}
      <section className="mx-auto max-w-7xl px-4 pb-14 md:px-6">
        <h2 className="mb-6 flex items-center gap-2 font-heading text-xl font-black text-foreground-950 md:text-2xl">
          <i className="ri-stack-line text-primary-500" aria-hidden="true" />
          本栏目最新瓜文
        </h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-3">
          {stories.map((s) => (
            <StoryCard key={s.slug} story={s} />
          ))}
        </div>
      </section>
    </>
  );
}